// One employee's ACA year, read from the engine snapshot
// (GET /api/aca/year/employee). Shared by the broker and employer employee
// detail pages so neither computes full-time status on its own.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, AlertCircle } from "lucide-react";
import { Help, TermLabel, Explained, CodeHelp } from "@/pages/employer/aca/shared/Help";
import type { GlossaryKey } from "@/pages/employer/aca/shared/acaGlossary";

interface Props {
  clientId: number | null | undefined;
  employeeId: number | string | null | undefined;
}

interface MonthRow {
  month: number;
  employed: boolean;
  acaStatus: string;
  hours: number | null;
  hoursSource: string | null;
  lnapReason: string | null;
  offered: boolean | null;
  offerPlanName: string | null;
  line14: string | null;
  line15: number | null;
  line16: string | null;
  enrolled: boolean;
  affordability: string;
  safeHarbor: string | null;
  threshold: number | null;
}

interface EmployeeYear {
  employeeId: number;
  formRequired: "full_time" | "self_insured_enrolled" | null;
  planStartMonth: string;
  months: MonthRow[];
  summary: { employedMonths: number; fullTimeMonths: number; lnapMonths: number; offeredMonths: number; enrolledMonths: number; affordableMonths: number; notAffordableMonths: number; pendingMonths: number; status: string };
  exceptions: Array<{ code: string; severity: string; message: string; month?: number }>;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUS_LABEL: Record<string, string> = {
  affordable: "Affordable", not_affordable: "Not Affordable", pending_data: "Pending Data", not_applicable: "Not Applicable", mixed: "Mixed (some months not affordable)",
};
const ACA_STATUS_LABEL: Record<string, string> = {
  full_time: "FT", non_full_time: "Non-FT", measuring: "Measuring", not_employed: "—", unknown: "?",
};

export function EmployeeAcaSummary({ clientId, employeeId }: Props) {
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const enabled = !!clientId && !!employeeId;

  const { data, isLoading, error } = useQuery<{ meta: { version: number; status: string; computedAt: string | null }; employee: EmployeeYear; ale: { isAle: boolean; estimated: boolean } }>({
    queryKey: ["/api/aca/year/employee", clientId, taxYear, employeeId],
    queryFn: async () => {
      const r = await fetch(`/api/aca/year/employee?clientId=${clientId}&taxYear=${taxYear}&employeeId=${employeeId}`, { credentials: "include" });
      if (r.status === 404) throw Object.assign(new Error("not_computed"), { notComputed: true });
      if (!r.ok) throw new Error("Failed to load ACA result");
      return r.json();
    },
    enabled,
    retry: false,
  });

  const notComputed = !!(error as any)?.notComputed;
  const e = data?.employee;

  return (
    <div className="space-y-6">
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader className="bg-purple-50 dark:bg-purple-950/20">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" /> ACA — tax year {taxYear}
            </CardTitle>
            <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
              <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {!enabled && <p className="text-sm text-muted-foreground">Employee is not linked to a client.</p>}
          {enabled && isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {enabled && notComputed && (
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <AlertCircle className="h-5 w-5 text-blue-600 shrink-0" />
              <span>Not computed for this year yet, or the employee was not employed during {taxYear}. Run the ACA module for {taxYear} to populate this view.</span>
            </div>
          )}
          {enabled && error && !notComputed && <p className="text-sm text-red-600">{(error as Error).message}</p>}
          {e && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="ACA full-time months" term="fullTime" value={`${e.summary.fullTimeMonths} / 12`} />
                <Stat label="Limited non-assessment months" term="lnap" value={String(e.summary.lnapMonths)} />
                <Stat label="Form 1095-C" term="formRequired" value={e.formRequired ? (e.formRequired === "full_time" ? "Required (full-time)" : "Required (self-insured enrollee)") : "Not required"} />
                <Stat label="Affordability" term={e.summary.status === "pending_data" ? "pendingData" : e.summary.status === "not_applicable" ? "notApplicable" : "affordability"} value={STATUS_LABEL[e.summary.status] ?? e.summary.status} />
                <Stat label="Employer ALE status" term={data?.ale?.estimated ? "aleEstimated" : "ale"} value={data?.ale ? `${data.ale.isAle ? "ALE" : "Not an ALE"}${data.ale.estimated ? " (estimated)" : ""}` : "—"} />
                <Stat label="Months offered coverage" term="offer" value={`${e.summary.offeredMonths}`} />
                <Stat label="Months enrolled" term="enrolledMonthForms" value={`${e.summary.enrolledMonths}`} />
                <Stat label="Plan start month" term="planStartMonth" value={e.planStartMonth} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted">
                    <tr><th className="text-left p-1 w-32"></th>{MONTHS.map((m) => <th key={m} className="p-1">{m}</th>)}</tr>
                  </thead>
                  <tbody>
                    <tr className="border-t"><td className="p-1"><TermLabel term="fullTime">ACA status</TermLabel></td>{e.months.map((m) => <td key={m.month} className="p-1 text-center">{m.employed ? (m.lnapReason ? <Explained term="lnap">{(ACA_STATUS_LABEL[m.acaStatus] ?? m.acaStatus) + "*"}</Explained> : (ACA_STATUS_LABEL[m.acaStatus] ?? m.acaStatus)) : "—"}</td>)}</tr>
                    <tr className="border-t"><td className="p-1"><TermLabel term="line14">Line 14</TermLabel></td>{e.months.map((m) => <td key={m.month} className="p-1 text-center font-mono">{m.line14 ? <CodeHelp code={m.line14} line={14} /> : ""}</td>)}</tr>
                    <tr className="border-t"><td className="p-1"><TermLabel term="line15">Line 15</TermLabel></td>{e.months.map((m) => <td key={m.month} className="p-1 text-center font-mono">{m.line15 == null ? "" : `$${m.line15.toFixed(0)}`}</td>)}</tr>
                    <tr className="border-t"><td className="p-1"><TermLabel term="line16">Line 16</TermLabel></td>{e.months.map((m) => <td key={m.month} className="p-1 text-center font-mono">{m.employed ? <CodeHelp code={m.line16 ?? null} line={16}>{m.line16 ?? <span className="text-muted-foreground">·</span>}</CodeHelp> : ""}</td>)}</tr>
                  </tbody>
                </table>
                <p className="text-[11px] text-muted-foreground mt-1">* <TermLabel term="lnap">Limited Non-Assessment Period</TermLabel>. Full detail, hours and <TermLabel term="showYourWork">"show your work"</TermLabel> are in the ACA module's Employees tab.</p>
              </div>
              {e.exceptions.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {e.exceptions.slice(0, 6).map((x, i) => <Badge key={i} variant={x.severity === "blocking" ? "destructive" : "outline"}>{x.message}</Badge>)}
                </div>
              )}
              {data?.meta && <p className="text-[11px] text-muted-foreground"><TermLabel term="snapshotVersion">Snapshot v{data.meta.version} ({data.meta.status})</TermLabel>{data.meta.computedAt ? ` · ${new Date(data.meta.computedAt).toLocaleString()}` : ""}</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, term }: { label: string; value: string; term?: GlossaryKey }) {
  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">{term ? <TermLabel term={term}>{label}</TermLabel> : label}</p>
      <p className="text-base font-medium">{value}</p>
    </div>
  );
}
